import { useState } from 'react';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ArrowLeft } from 'lucide-react';
import { api, errorMessage } from '@/lib/api/client';
import type { Vendor } from '@/lib/api/types';
import { Page, PageBody, PageHeader, Panel } from '@/ui/Panel';
import { Button } from '@/ui/Button';
import { Field, Input, Switch, Textarea } from '@/ui/Field';
import { useShortcutHint } from '@/app/StatusLine';
import { vendorKeys } from '@/features/buying/api';

export const Route = createFileRoute('/_app/vendors/new')({
  staticData: { crumb: 'New vendor' },
  component: NewVendor,
});

const FIELDS = [
  ['name', 'Name', true],
  ['contactName', 'Contact', false],
  ['phone', 'Phone', false],
  ['phoneExt', 'Extension', false],
  ['fax', 'Fax', false],
  ['email', 'Email', false],
  ['terms', 'Terms', false],
  ['addressLine1', 'Address line 1', false],
  ['addressLine2', 'Address line 2', false],
  ['city', 'City', false],
  ['state', 'State', false],
  ['zip', 'ZIP', false],
] as const;

type FieldKey = (typeof FIELDS)[number][0];

function NewVendor() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [form, setForm] = useState<Record<FieldKey, string>>(() => {
    const out = {} as Record<FieldKey, string>;
    for (const [key] of FIELDS) out[key] = '';
    return out;
  });
  const [notes, setNotes] = useState('');
  const [parsingNotes, setParsingNotes] = useState('');
  const [forcePdf, setForcePdf] = useState(false);

  useShortcutHint([{ keys: 'Esc', label: 'back to vendors' }]);

  const create = useMutation({
    mutationFn: () =>
      api.post<Vendor>('/vendors', {
        ...Object.fromEntries(
          FIELDS.map(([key]) => [key, form[key].trim()]).filter(
            ([, value]) => value !== ''
          )
        ),
        ...(notes.trim() ? { notes: notes.trim() } : {}),
        ...(parsingNotes.trim() ? { invoiceParsingNotes: parsingNotes.trim() } : {}),
        forcePdfFileMode: forcePdf,
      }),
    onSuccess: (vendor) => {
      void queryClient.invalidateQueries({ queryKey: vendorKeys.all });
      toast.success(`${vendor.name} added.`);
      void navigate({
        to: '/vendors/$id',
        params: { id: String(vendor.id) },
        search: { tab: 'catalog' as const },
      });
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const valid = form.name.trim() !== '';

  return (
    <Page>
      <PageHeader
        eyebrow={
          <Link to="/vendors" search={{}} className="hover:text-ink">
            Vendors
          </Link>
        }
        title="New vendor"
        subtitle="Only the name is required — the rest can follow."
        actions={
          <>
            <Button
              size="sm"
              variant="ghost"
              asChild
              icon={<ArrowLeft className="size-3.5" />}
            >
              <Link to="/vendors" search={{}}>
                Cancel
              </Link>
            </Button>
            <Button
              size="sm"
              variant="primary"
              disabled={!valid}
              loading={create.isPending}
              onClick={() => create.mutate()}
            >
              Create vendor
            </Button>
          </>
        }
      />

      <PageBody>
        <div className="grid gap-4 lg:grid-cols-2">
          <Panel title="Details">
            <div className="grid gap-3 sm:grid-cols-2">
              {FIELDS.map(([key, label, required]) => (
                <Field
                  key={key}
                  label={label}
                  required={required}
                  className={
                    key === 'addressLine1' || key === 'addressLine2'
                      ? 'sm:col-span-2'
                      : ''
                  }
                >
                  <Input
                    size="sm"
                    autoFocus={key === 'name'}
                    value={form[key]}
                    invalid={required && form[key].trim() === ''}
                    onChange={(e) => {
                      const value = e.currentTarget.value;
                      setForm((prev) => ({ ...prev, [key]: value }));
                    }}
                  />
                </Field>
              ))}
            </div>
          </Panel>

          <div className="space-y-4">
            <Panel title="Internal notes">
              <Textarea
                rows={5}
                value={notes}
                placeholder="Ordering days, minimums, who to call…"
                onChange={(e) => setNotes(e.currentTarget.value)}
              />
            </Panel>

            <Panel
              title="Invoice reading"
              description="Optional. Tells the AI parser how this vendor's paperwork is laid out."
            >
              <div className="space-y-3">
                <Textarea
                  rows={5}
                  value={parsingNotes}
                  placeholder="e.g. Quantities are in cases; the unit price column is per case."
                  onChange={(e) => setParsingNotes(e.currentTarget.value)}
                />
                <Switch
                  checked={forcePdf}
                  onCheckedChange={setForcePdf}
                  label="Always parse PDFs as images"
                />
              </div>
            </Panel>
          </div>
        </div>
      </PageBody>
    </Page>
  );
}
